import React, { useState, useEffect } from "react";
import {
  FaMicrophone, FaCalendarAlt, FaClock, FaMapMarkerAlt,
  FaCheckCircle, FaTimes, FaStickyNote, FaFileAlt, FaArrowRight,
  FaShieldAlt
} from "react-icons/fa";
import stateCityData from "../../../data/states.json";

const FreePressConference = () => {
  const [formData, setFormData] = useState({
    topic: "", purpose: "", conferenceDate: "", conferenceTime: "",
    timePeriod: "AM", state: "", city: "", place: "", landmark: "",
    adminNote: "", termsAccepted: false
  });

  const [states] = useState(Object.keys(stateCityData));
  const [cities, setCities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [alertProgress, setAlertProgress] = useState(100);

  const wordCount = formData.purpose.trim().split(/\s+/).filter(w => w.length > 0).length;

  useEffect(() => {
    if (!showErrorAlert) return;
    setAlertProgress(100);
    const interval = setInterval(() => {
      setAlertProgress(p => {
        if (p <= 0) { setShowErrorAlert(false); return 100; }
        return p - 2;
      });
    }, 100);
    const timer = setTimeout(() => { setShowErrorAlert(false); setAlertProgress(100); }, 5000);
    return () => { clearInterval(interval); clearTimeout(timer); };
  }, [showErrorAlert]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updated = { ...formData, [name]: type === "checkbox" ? checked : value };
    if (name === "conferenceTime" && value) {
      updated.timePeriod = parseInt(value.split(":")[0], 10) >= 12 ? "PM" : "AM";
    }
    setFormData(updated);
    setShowErrorAlert(false);
  };

  const handleStateChange = (e) => {
    const s = e.target.value;
    setFormData({ ...formData, state: s, city: "" });
    setCities(s ? (stateCityData[s] || []) : []);
  };

  const showError = (msg) => { setErrorMessage(msg); setShowErrorAlert(true); };

  const validate = () => {
    const req = [
      { key: "topic", label: "Topic" }, { key: "purpose", label: "Purpose" },
      { key: "conferenceDate", label: "Date" }, { key: "conferenceTime", label: "Time" },
      { key: "state", label: "State" }, { key: "city", label: "City" },
      { key: "place", label: "Venue" }, { key: "landmark", label: "Address" }
    ];
    const missing = req.filter(f => !formData[f.key]);
    if (missing.length) { showError(`Missing: ${missing.map(f => f.label).join(", ")}`); return false; }
    if (wordCount < 10) { showError("Purpose must be at least 10 words"); return false; }
    if (!formData.termsAccepted) { showError("Please accept the terms and conditions"); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("pressToken");
      const apiUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
      const res = await fetch(`${apiUrl}/press/free-conference/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.success) {
        setSuccessMessage(`Conference submitted! ID: ${result.data.conferenceId}`);
        setShowSuccessModal(true);
        setFormData({ topic: "", purpose: "", conferenceDate: "", conferenceTime: "", timePeriod: "AM", state: "", city: "", place: "", landmark: "", adminNote: "", termsAccepted: false });
        setCities([]);
        setTimeout(() => { window.location.href = "/press/conference-status"; }, 2500);
      } else {
        showError(result.message || "Failed to submit. Try again.");
      }
    } catch {
      showError("Network error. Please try again.");
    }
    setIsSubmitting(false);
  };

  const inputCls = (isRequired, val) =>
    `w-full px-3 py-2.5 text-sm bg-white border ${isRequired && !val ? "border-red-200 focus:ring-red-400" : "border-slate-200 focus:ring-emerald-400"} rounded-xl shadow-sm focus:ring-2 focus:border-transparent outline-none transition-all`;

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8">

      {/* ── Floating Error Toast ── */}
      {showErrorAlert && (
        <div className="fixed top-3 right-3 left-3 sm:left-auto sm:right-4 sm:top-4 z-50 sm:max-w-sm">
          <div className="bg-rose-600 text-white px-4 py-3 rounded-2xl shadow-2xl relative overflow-hidden flex items-start gap-3">
            <div className="absolute bottom-0 left-0 h-0.5 bg-rose-300 transition-all duration-100" style={{ width: `${alertProgress}%` }} />
            <FaTimes className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-semibold flex-1 leading-snug">{errorMessage}</p>
            <button onClick={() => setShowErrorAlert(false)} className="flex-shrink-0 text-rose-200 hover:text-white">
              <FaTimes className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="relative bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 rounded-[1.5rem] p-4 sm:p-8 mb-4 overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/5" />
        <div className="absolute bottom-0 left-1/3 w-40 h-40 rounded-full bg-white/5 translate-y-1/2" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center flex-shrink-0">
            <FaMicrophone className="text-white w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div>
            <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Press Conference</p>
            <h1 className="text-lg sm:text-2xl font-black text-white tracking-tight leading-tight">Free Conference</h1>
            <p className="text-white/70 text-xs font-medium mt-0.5 hidden sm:block">Submit your free press conference details</p>
          </div>
        </div>
      </div>

      {/* ── Guidelines Banner ── */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 sm:p-4 mb-4">
        <p className="text-blue-700 text-[11px] font-black uppercase tracking-widest mb-2">Guidelines</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {[
            "Free for public interest topics",
            "Reviewed & approved within 24 hrs",
            "Open to all media representatives",
            "Ensure all details are accurate"
          ].map((g, i) => (
            <div key={i} className="flex items-center gap-1.5 text-blue-700 text-xs font-medium">
              <FaCheckCircle className="w-3 h-3 text-blue-400 flex-shrink-0" />
              {g}
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Form ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <form onSubmit={handleSubmit}>

          {/* Section: Basic Info */}
          <div className="px-4 py-3 bg-gradient-to-r from-slate-700 to-slate-800 flex items-center gap-2">
            <FaFileAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Basic Information</span>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {/* Topic */}
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                Topic of Conference <span className="text-rose-400">*</span>
              </label>
              <input
                type="text" name="topic" value={formData.topic} onChange={handleChange}
                className={inputCls(true, formData.topic)}
                placeholder="Enter the topic of your press conference"
              />
            </div>

            {/* Purpose */}
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>Press Note / Purpose <span className="text-rose-400">*</span></span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${wordCount >= 10 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                  {wordCount}/10 words
                </span>
              </label>
              <textarea
                name="purpose" value={formData.purpose} onChange={handleChange} rows={4}
                className={`${inputCls(true, formData.purpose)} resize-none`}
                placeholder="Briefly describe the purpose of your press conference (minimum 10 words)"
              />
            </div>
          </div>

          {/* Section: Schedule */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-700 to-indigo-800 flex items-center gap-2">
            <FaCalendarAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Schedule</span>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <FaCalendarAlt className="w-3 h-3 text-blue-500" /> Date <span className="text-rose-400">*</span>
                </label>
                <input
                  type="date" name="conferenceDate" value={formData.conferenceDate} onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                  className={inputCls(true, formData.conferenceDate)}
                />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <FaClock className="w-3 h-3 text-blue-500" /> Time <span className="text-rose-400">*</span>
                </label>
                <input
                  type="time" name="conferenceTime" value={formData.conferenceTime} onChange={handleChange}
                  className={inputCls(true, formData.conferenceTime)}
                />
              </div>
            </div>
          </div>

          {/* Section: Location */}
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 flex items-center gap-2">
            <FaMapMarkerAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Location</span>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {/* State */}
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                  State <span className="text-rose-400">*</span>
                </label>
                <select
                  name="state" value={formData.state} onChange={handleStateChange}
                  className={inputCls(true, formData.state)}
                >
                  <option value="">Select State</option>
                  {states.map((s, i) => <option key={i} value={s}>{s}</option>)}
                </select>
              </div>
              {/* City */}
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                  City <span className="text-rose-400">*</span>
                </label>
                <select
                  name="city" value={formData.city} onChange={handleChange}
                  className={`${inputCls(true, formData.city)} ${!formData.state ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={!formData.state}
                >
                  <option value="">Select City</option>
                  {cities.map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Venue */}
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                Venue / Place Name <span className="text-rose-400">*</span>
              </label>
              <input
                type="text" name="place" value={formData.place} onChange={handleChange}
                className={inputCls(true, formData.place)}
                placeholder="Enter the full venue name"
              />
            </div>

            {/* Landmark / Address */}
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                Address / Landmark (pincode required) <span className="text-rose-400">*</span>
              </label>
              <input
                type="text" name="landmark" value={formData.landmark} onChange={handleChange}
                className={inputCls(true, formData.landmark)}
                placeholder="Enter full address with pincode"
              />
            </div>
          </div>

          {/* Section: Admin Note */}
          <div className="px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-700 flex items-center gap-2">
            <FaStickyNote className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Note for Admin</span>
            <span className="ml-auto text-white/50 text-[9px] font-bold uppercase">Optional</span>
          </div>
          <div className="p-4 sm:p-6">
            <textarea
              name="adminNote" value={formData.adminNote} onChange={handleChange} rows={3}
              className={`${inputCls(false, formData.adminNote)} resize-none`}
              placeholder="Add any special requirements or additional info for the admin..."
            />
            <p className="text-[10px] text-slate-400 mt-1.5 font-medium">Visible to administrators for review purposes</p>
          </div>

          {/* Terms + Submit */}
          <div className="px-4 sm:px-6 pb-6 space-y-4">
            {/* Terms */}
            <label className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl cursor-pointer border border-transparent hover:border-slate-200 transition-all">
              <input
                type="checkbox" id="termsAccepted" name="termsAccepted"
                checked={formData.termsAccepted} onChange={handleChange}
                className="mt-0.5 w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 flex-shrink-0"
              />
              <span className="text-xs text-slate-600 leading-relaxed font-medium">
                <span className="text-slate-900 font-black">I confirm: </span>
                I have read all details and agree to the terms and conditions of the Free Press Conference.
              </span>
            </label>

            {/* Submit Button */}
            <button
              type="submit" disabled={isSubmitting}
              className={`w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl text-sm font-black text-white shadow-lg transition-all active:scale-[0.98] ${isSubmitting
                  ? "bg-slate-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-emerald-500 to-green-600 shadow-emerald-200 hover:shadow-emerald-300 hover:shadow-xl"
                }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <FaCheckCircle className="w-4 h-4" />
                  Submit Free Conference
                  <FaArrowRight className="w-3 h-3" />
                </>
              )}
            </button>

            {/* Security note */}
            <p className="text-center text-[10px] text-slate-400 font-medium flex items-center justify-center gap-1.5">
              <FaShieldAlt className="w-3 h-3 text-emerald-400" />
              Secure submission · Reviewed within 24 hours
            </p>
          </div>
        </form>
      </div>

      {/* ── Success Modal ── */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowSuccessModal(false)} />
          <div className="relative bg-white rounded-[2rem] p-6 sm:p-8 w-full max-w-sm shadow-2xl border border-white/20 text-center space-y-4 z-10">
            {/* Success Icon */}
            <div className="relative w-16 h-16 mx-auto">
              <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-50" />
              <div className="relative w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-200">
                <FaCheckCircle className="text-white w-8 h-8" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-800">Submitted!</h3>
              <p className="text-slate-500 text-sm mt-1 leading-relaxed">{successMessage}</p>
              <p className="text-emerald-600 text-xs font-bold mt-2">Redirecting to status page...</p>
            </div>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 bg-emerald-500 text-white text-sm font-black rounded-2xl shadow-lg shadow-emerald-100 active:scale-95 transition-all"
            >
              OK, Great! 🎉
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreePressConference;
